import { useState } from 'react';
import type { ResumeData, ResumeAnalysis, ResumeSettings, TailorResult } from '../types/resume';
import {
  analyzeResume,
  tailorResume,
  generateWithAI,
  generateCoverLetter,
  generateInterviewPrep,
  scanAts,
  translateResume,
  generateLinkedIn,
  generatePortfolio,
  type GenerateType,
  type AtsScanResult,
  type InterviewPrepResult,
} from '../lib/api';
import {
  Sparkles,
  Wand2,
  AlertCircle,
  CheckCircle,
  Target,
  TrendingUp,
  Loader2,
  FileText,
  Briefcase,
  Zap,
  Lightbulb,
  Languages,
  MessageSquare,
  ListChecks,
  ScanSearch,
  GraduationCap,
  Linkedin,
  HelpCircle,
  Type,
  Maximize2,
  Minimize2,
  BarChart3,
  Copy,
  Globe,
} from 'lucide-react';

interface AiPanelProps {
  data: ResumeData;
  settings?: ResumeSettings;
  onApplyTailor: (result: TailorResult) => void;
  onUpdateSummary: (summary: string) => void;
}

type Tab = 'analyze' | 'tailor' | 'writer';
type WriterTool =
  | 'cover-letter'
  | 'tone-shift'
  | 'expand'
  | 'shorten'
  | 'quantify'
  | 'interview-prep'
  | 'linkedin-about'
  | 'linkedin-profile'
  | 'portfolio'
  | 'cert-suggestions'
  | 'translate'
  | 'jd-skills'
  | 'ats-scan';

const priorityClass: Record<string, string> = {
  high: 'bg-red-50 text-red-700 border-red-100',
  medium: 'bg-amber-50 text-amber-700 border-amber-100',
  low: 'bg-blue-50 text-blue-700 border-blue-100',
};

const categoryLabels: Record<string, string> = {
  impact: 'Impact & Achievements',
  brevity: 'Brevity',
  style: 'Style & Format',
  skills: 'Skills',
  sections: 'Completeness',
};

const tools: { key: WriterTool; label: string; icon: React.ElementType }[] = [
  { key: 'cover-letter', label: 'Cover Letter', icon: MessageSquare },
  { key: 'tone-shift', label: 'Tone Shift', icon: Type },
  { key: 'expand', label: 'Expand', icon: Maximize2 },
  { key: 'shorten', label: 'Shorten', icon: Minimize2 },
  { key: 'quantify', label: 'Quantify', icon: BarChart3 },
  { key: 'interview-prep', label: 'Interview Prep', icon: HelpCircle },
  { key: 'linkedin-about', label: 'LinkedIn About', icon: Linkedin },
  { key: 'linkedin-profile', label: 'LinkedIn Profile', icon: Linkedin },
  { key: 'portfolio', label: 'Portfolio Site', icon: Globe },
  { key: 'cert-suggestions', label: 'Cert Suggestions', icon: GraduationCap },
  { key: 'jd-skills', label: 'JD Skills', icon: ListChecks },
  { key: 'translate', label: 'Translate', icon: Languages },
  { key: 'ats-scan', label: 'ATS Scanner', icon: ScanSearch },
];

const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'analyze', label: 'Analyze', icon: TrendingUp },
  { key: 'tailor', label: 'Tailor', icon: Target },
  { key: 'writer', label: 'Tools', icon: Wand2 },
];

const tones = ['formal', 'executive', 'technical', 'friendly'];
const languages = ['Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Arabic', 'Portuguese', 'Italian'];

function ScoreRing({ score, size = 80 }: { score: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? 'stroke-emerald-500' : score >= 60 ? 'stroke-amber-500' : 'stroke-red-500';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} className="stroke-slate-100" strokeWidth="8" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={`${color} transition-all duration-700`}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-slate-800">{score}</span>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">/100</span>
      </div>
    </div>
  );
}

function CategoryBar({ label, value }: { label: string; value: number }) {
  const color = value >= 80 ? 'bg-emerald-500' : value >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-slate-800">{value}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
    >
      <Copy className="h-3 w-3" /> {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function AiPanel({ data, onApplyTailor, onUpdateSummary }: AiPanelProps) {
  const [tab, setTab] = useState<Tab>('analyze');
  const [analysis, setAnalysis] = useState<ResumeAnalysis | null>(null);
  const [tailorResult, setTailorResult] = useState<TailorResult | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Writer state
  const [writerTool, setWriterTool] = useState<WriterTool>('cover-letter');
  const [writerInput, setWriterInput] = useState('');
  const [writerTone, setWriterTone] = useState('formal');
  const [writerLanguage, setWriterLanguage] = useState('Spanish');
  const [linkedinFocus, setLinkedinFocus] = useState('');
  const [portfolioTheme, setPortfolioTheme] = useState('modern');
  const [writerResult, setWriterResult] = useState<string | string[] | AtsScanResult | InterviewPrepResult | { headline: string; about: string; skills: string[] } | null>(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const { result } = await analyzeResume(data);
      setAnalysis(result);
    } catch (err: any) {
      setError(err.message || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTailor = async () => {
    if (jobDescription.length < 50) {
      setError('Please paste a job description with at least 50 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { result } = await tailorResume(data, jobDescription);
      setTailorResult(result);
    } catch (err: any) {
      setError(err.message || 'Tailoring failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyTailor = () => {
    if (!tailorResult) return;
    onApplyTailor(tailorResult);
    setTailorResult(null);
    setJobDescription('');
    setTab('analyze');
  };

  const handleWriterAction = async () => {
    setLoading(true);
    setError(null);
    setWriterResult(null);
    try {
      if (writerTool === 'cover-letter') {
        if (jobDescription.length < 50) {
          setError('Paste a job description with at least 50 characters.');
          setLoading(false);
          return;
        }
        const { result } = await generateCoverLetter(data, jobDescription);
        setWriterResult(result);
      } else if (writerTool === 'interview-prep') {
        const { result } = await generateInterviewPrep(data, jobDescription || undefined);
        setWriterResult(result);
      } else if (writerTool === 'ats-scan') {
        if (jobDescription.length < 50) {
          setError('Paste a job description with at least 50 characters.');
          setLoading(false);
          return;
        }
        const { result } = await scanAts(data, jobDescription);
        setWriterResult(result);
      } else if (writerTool === 'translate') {
        const { result } = await translateResume(data, writerLanguage);
        setWriterResult(result);
      } else if (writerTool === 'linkedin-profile') {
        const { result } = await generateLinkedIn(data, linkedinFocus.trim() || undefined);
        setWriterResult(result);
      } else if (writerTool === 'portfolio') {
        const { result } = await generatePortfolio(data, portfolioTheme);
        setWriterResult(result);
      } else {
        const type: GenerateType = writerTool === 'jd-skills' ? 'jd-skills'
          : writerTool === 'cert-suggestions' ? 'cert-suggestions'
          : writerTool === 'linkedin-about' ? 'linkedin-about'
          : writerTool === 'expand' ? 'expand'
          : writerTool === 'shorten' ? 'shorten'
          : writerTool === 'tone-shift' ? 'tone-shift'
          : 'quantify';
        const { result } = await generateWithAI({
          type,
          resume: data,
          context: writerInput,
          tone: writerTool === 'tone-shift' ? writerTone : undefined,
        });
        setWriterResult(result);
      }
    } catch (err: any) {
      setError(err.message || 'Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const needsJobDescription = ['cover-letter', 'interview-prep', 'ats-scan', 'jd-skills'].includes(writerTool);
  const needsTextInput = ['expand', 'shorten', 'tone-shift', 'quantify'].includes(writerTool);

  return (
    <div className="rx-dark h-full flex flex-col bg-white border-l">
      {/* Header */}
      <div className="px-5 py-4 border-b bg-slate-50">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-primary-100 rounded-lg">
            <Sparkles className="h-5 w-5 text-primary-600" />
          </div>
          <h2 className="font-bold text-slate-900">AI Assistant</h2>
        </div>
        <div className="rx-seg" role="tablist" aria-label="AI assistant tools">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className="rx-seg-btn"
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {tab === 'analyze' && (
          <>
            {!analysis && (
              <div className="text-center py-8 space-y-4">
                <div className="mx-auto w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-primary-600" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-slate-900">Analyze your resume</h3>
                  <p className="text-sm text-slate-500 px-4">
                    Get an ATS score, strengths, and actionable improvements in seconds.
                  </p>
                </div>
                <button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {loading ? 'Analyzing...' : 'Analyze Resume'}
                </button>
              </div>
            )}

            {analysis && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <ScoreRing score={analysis.score} />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">
                      {analysis.score >= 80
                        ? 'Strong resume'
                        : analysis.score >= 60
                        ? 'Good foundation'
                        : 'Needs improvement'}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{analysis.summary}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {Object.entries(analysis.categoryScores).map(([key, value]) => (
                    <CategoryBar key={key} label={categoryLabels[key] || key} value={value} />
                  ))}
                </div>

                {analysis.strengths.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                      <CheckCircle className="h-3.5 w-3.5" /> Strengths
                    </h4>
                    <ul className="space-y-2">
                      {analysis.strengths.map((strength, idx) => (
                        <li key={idx} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-emerald-500 mt-0.5 shrink-0">•</span>
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.improvements.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                      <Lightbulb className="h-3.5 w-3.5" /> Improvements
                    </h4>
                    <div className="space-y-2">
                      {analysis.improvements.map((item, idx) => (
                        <div
                          key={idx}
                          className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-medium text-slate-800">{item.title}</span>
                            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${priorityClass[item.priority]}`}>
                              {item.priority}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.missingKeywords.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Keywords to consider
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {analysis.missingKeywords.map((kw, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  Re-analyze
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'tailor' && (
          <>
            {!tailorResult ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-primary-600" /> Tailor to a job
                  </h3>
                  <p className="text-sm text-slate-500">
                    Paste a job description and AI will adapt your resume to match it.
                  </p>
                </div>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the job description here..."
                  rows={12}
                  className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <button
                  onClick={handleTailor}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {loading ? 'Tailoring...' : 'Tailor Resume'}
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <ScoreRing score={tailorResult.matchScore} />
                  <div>
                    <div className="text-sm font-medium text-slate-900">Job match score</div>
                    <p className="text-xs text-slate-500">{tailorResult.reasoning}</p>
                  </div>
                </div>

                {tailorResult.keywordGaps.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Keyword gaps</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {tailorResult.keywordGaps.map((kw, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-100"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {tailorResult.tailoredSummary && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Suggested summary</h4>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                      {tailorResult.tailoredSummary}
                    </div>
                    <button
                      onClick={() => onUpdateSummary(tailorResult.tailoredSummary)}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700"
                    >
                      Use this summary →
                    </button>
                  </div>
                )}

                {tailorResult.suggestedSkills.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Suggested skills to add</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {tailorResult.suggestedSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary-50 text-primary-700 border border-primary-100"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <button
                    onClick={applyTailor}
                    className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Wand2 className="h-4 w-4" /> Apply all suggestions
                  </button>
                  <button
                    onClick={() => setTailorResult(null)}
                    className="w-full text-xs text-slate-500 hover:text-slate-700 py-1"
                  >
                    Back to job description
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'writer' && (
          <div className="space-y-4">
            <div className="space-y-1">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-primary-600" /> AI writing tools
              </h3>
              <p className="text-xs text-slate-500">Choose a tool and generate content from your resume.</p>
            </div>

            <select
              value={writerTool}
              onChange={(e) => {
                setWriterTool(e.target.value as WriterTool);
                setWriterResult(null);
                setError(null);
              }}
              className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
            >
              {tools.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>

            {needsJobDescription && (
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the job description here..."
                rows={6}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            )}

            {needsTextInput && (
              <textarea
                value={writerInput}
                onChange={(e) => setWriterInput(e.target.value)}
                placeholder="Paste the text you want to rewrite..."
                rows={6}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            )}

            {writerTool === 'tone-shift' && (
              <select
                value={writerTone}
                onChange={(e) => setWriterTone(e.target.value)}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
              >
                {tones.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            )}

            {writerTool === 'translate' && (
              <select
                value={writerLanguage}
                onChange={(e) => setWriterLanguage(e.target.value)}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
              >
                {languages.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            )}

            {writerTool === 'linkedin-profile' && (
              <input
                type="text"
                value={linkedinFocus}
                onChange={(e) => setLinkedinFocus(e.target.value)}
                placeholder="e.g. senior engineering roles"
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
            )}
            {writerTool === 'portfolio' && (
              <select
                value={portfolioTheme}
                onChange={(e) => setPortfolioTheme(e.target.value)}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
              >
                <option value="modern">Modern</option>
                <option value="dark">Dark</option>
                <option value="minimal">Minimal</option>
                <option value="colorful">Colorful</option>
              </select>
            )}

            <button
              onClick={handleWriterAction}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? 'Generating...' : `Generate ${tools.find((t) => t.key === writerTool)?.label}`}
            </button>

            {writerResult && typeof writerResult === 'object' && 'headline' in writerResult && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">LinkedIn Profile</h4>
                  <CopyButton text={`${writerResult.headline}\n\n${writerResult.about}\n\nSkills: ${writerResult.skills.join(', ')}`} />
                </div>
                <div className="space-y-2">
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                    <span className="text-xs font-semibold text-slate-500 uppercase">Headline</span>
                    <p className="text-slate-800 font-medium mt-1">{writerResult.headline}</p>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                    <span className="text-xs font-semibold text-slate-500 uppercase">About</span>
                    <p className="text-slate-700 mt-1 whitespace-pre-line">{writerResult.about}</p>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                    <span className="text-xs font-semibold text-slate-500 uppercase">Skills</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {writerResult.skills.map((skill, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary-50 text-primary-700 border border-primary-100">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {writerTool === 'portfolio' && typeof writerResult === 'string' && writerResult && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Portfolio Preview</h4>
                  <CopyButton text={writerResult} />
                </div>
                <iframe
                  title="Portfolio preview"
                  srcDoc={writerResult}
                  className="w-full h-64 rounded-lg border border-slate-200 bg-white"
                  sandbox="allow-scripts"
                />
                <a
                  href={URL.createObjectURL(new Blob([writerResult], { type: 'text/html' }))}
                  download="portfolio.html"
                  className="inline-flex items-center gap-2 text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  Download HTML
                </a>
              </div>
            )}

            {writerResult && typeof writerResult === 'string' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Result</h4>
                  <CopyButton text={writerResult} />
                </div>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line">
                  {writerResult}
                </div>
              </div>
            )}

            {writerResult && Array.isArray(writerResult) && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Result</h4>
                <div className="flex flex-wrap gap-1.5">
                  {writerResult.map((item, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {writerResult && typeof writerResult === 'object' && 'score' in writerResult && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <ScoreRing score={(writerResult as AtsScanResult).score} />
                  <div>
                    <div className="text-sm font-medium text-slate-900">ATS match score</div>
                    <p className="text-xs text-slate-500">{(writerResult as AtsScanResult).reasoning}</p>
                  </div>
                </div>
                {(writerResult as AtsScanResult).presentKeywords.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Present keywords</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {(writerResult as AtsScanResult).presentKeywords.map((kw, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-100">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(writerResult as AtsScanResult).missingKeywords.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Missing keywords</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {(writerResult as AtsScanResult).missingKeywords.map((kw, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-100">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {writerResult && typeof writerResult === 'object' && 'questions' in writerResult && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Interview prep</h4>
                {(writerResult as InterviewPrepResult).questions.map((q, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm space-y-1">
                    <p className="font-medium text-slate-900">Q{idx + 1}. {q.question}</p>
                    <p className="text-xs text-slate-600">{q.suggestedAnswer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
